import dayjs, { Dayjs } from 'dayjs';
import { PaylinkPaymentMethodType, PaylinkStatus } from 'skipify-types';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { sanitizeNumericString } from '../pages/Paylinks/SendPaylinkForm/formValues';
import {
  DownloadPaylinksBody,
  GetAllPaylinksQueryFilters,
  paylinkCustomerSchema,
} from '../store/modules/paylink/types';

/** UI checkbox values. Mapped to API paymentMethodType + isFlexPay. */
export const PAYLINK_FILTER_PAYMENT_METHOD_TYPES = ['card', 'bank_account', 'flex_pay'] as const;
export type PaylinkPaymentMethodFilterType = (typeof PAYLINK_FILTER_PAYMENT_METHOD_TYPES)[number];

export type PaylinkPaymentMethodApiFilters = {
  paymentMethodType?: PaylinkPaymentMethodType[];
  isFlexPay?: boolean;
};

/**
 * Map UI Card / Bank / FlexPay checkboxes to API params. Both are stored as `card`,
 * so `isFlexPay` is what separates them:
 * - Card only            → paymentMethodType=card, isFlexPay=false
 * - FlexPay only         → isFlexPay=true
 * - Card and FlexPay     → paymentMethodType=card, isFlexPay omitted (all cards)
 * - Bank                 → paymentMethodType=bank_account (combines with the above)
 */
export const toPaylinkPaymentMethodApiFilters = (
  uiTypes: readonly PaylinkPaymentMethodFilterType[],
): PaylinkPaymentMethodApiFilters => {
  const wantsCard = uiTypes.includes('card');
  const wantsBank = uiTypes.includes('bank_account');
  const wantsFlex = uiTypes.includes('flex_pay');

  const paymentMethodType: PaylinkPaymentMethodType[] = [
    ...(wantsBank ? (['bank_account'] as const) : []),
    ...(wantsCard ? (['card'] as const) : []),
  ];
  // Card and FlexPay agreeing means we don't need to split the card rows at all:
  // both checked = every card, neither checked = no card filter to apply.
  const isFlexPay = wantsCard === wantsFlex ? undefined : wantsFlex;

  return {
    ...(paymentMethodType.length > 0 ? { paymentMethodType } : {}),
    ...(isFlexPay !== undefined ? { isFlexPay } : {}),
  };
};

const parseOptionalBoolean = (value?: boolean | string | null) => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
};

/** Inverse of toPaylinkPaymentMethodApiFilters for URL → form hydration. */
export const fromPaylinkPaymentMethodApiFilters = ({
  paymentMethodType,
  isFlexPay,
}: {
  paymentMethodType?: readonly string[] | string | null;
  isFlexPay?: boolean | string | null;
}): PaylinkPaymentMethodFilterType[] => {
  const types = Array.isArray(paymentMethodType)
    ? paymentMethodType
    : typeof paymentMethodType === 'string' && paymentMethodType
      ? paymentMethodType.split(',')
      : [];
  const flex = parseOptionalBoolean(isFlexPay);
  const hasCard = types.includes('card');

  return [
    ...(types.includes('bank_account') ? (['bank_account'] as const) : []),
    ...(hasCard ? (['card'] as const) : []),
    // isFlexPay=true is FlexPay on its own. Cards with no isFlexPay means the API
    // wasn't asked to split them, which is how we send Card and FlexPay together.
    ...(flex === true || (hasCard && flex === undefined) ? (['flex_pay'] as const) : []),
  ];
};

/**
 * Build filter checkboxes from whether bank_account / Flex Pay paylinks exist
 * (probed via GET paylinks pageSize=1). Card is included whenever either alternate exists.
 */
export const getVisiblePaylinkPaymentMethodFilterTypesFromHistory = ({
  hasBankAccountPaylinks,
  hasFlexPayPaylinks,
}: {
  hasBankAccountPaylinks: boolean;
  hasFlexPayPaylinks: boolean;
}): PaylinkPaymentMethodFilterType[] => {
  if (!hasBankAccountPaylinks && !hasFlexPayPaylinks) {
    return [];
  }
  return PAYLINK_FILTER_PAYMENT_METHOD_TYPES.filter((type) => {
    if (type === 'bank_account') return hasBankAccountPaylinks;
    if (type === 'flex_pay') return hasFlexPayPaylinks;
    return true;
  });
};

/** Hide the Payment method section when Card would be the only option (or none). */
export const shouldShowPaylinkPaymentMethodFilterSection = (
  visibleTypes: readonly PaylinkPaymentMethodFilterType[],
) => visibleTypes.length > 1;

const AmountSchema = z.string().refine(
  (amount) => {
    if (!amount) return true;
    const amountNumber = Number(sanitizeNumericString(amount as string));
    return !Number.isNaN(amountNumber) && amountNumber > 0 && amountNumber <= 2 ** 31 - 1;
  },
  {
    message: 'Invalid amount',
  },
);

const FILTER_STATUSES: readonly PaylinkStatus[] = [
  'SENT',
  'OPENED',
  'PAID',
  'PARTIALLY_PAID',
  'CANCELLED',
  'EXPIRED',
  'FAILED',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
  'RETURNED',
];

/** Statuses the API accepts together with a transaction date range. */
const TRANSACTED_FILTER_STATUSES: readonly PaylinkStatus[] = [
  'PAID',
  'PARTIALLY_PAID',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
  'RETURNED',
];

const getDateSchema = (
  boundary: 'startOf' | 'endOf',
  field: string,
  requiredFields: string[],
  message: string,
) =>
  z.preprocess(
    (val) => (val ? dayjs(val as Dayjs)[boundary]('day') : null),
    z
      .instanceof(dayjs as unknown as typeof Dayjs)
      .optional()
      .nullish()
      .refine((val) => !requiredFields?.includes(field) || !!val, { message }),
  );

export type PaylinkFiltersDateRangeRequirement = 'createdOrTransactionExclusive';

const getValidationSchema = (
  maxDateRange: Props['maxDateRange'],
  requiredFields: string[],
  dateRangeRequirement?: PaylinkFiltersDateRangeRequirement,
) => {
  const validationSchema = z
    .object({
      createdAfter: getDateSchema(
        'startOf',
        'createdAfter',
        requiredFields,
        'Start date is required',
      ),
      createdBefore: getDateSchema(
        'endOf',
        'createdBefore',
        requiredFields,
        'End date is required',
      ),
      transactedAfter: getDateSchema(
        'startOf',
        'transactedAfter',
        requiredFields,
        'Start date is required',
      ),
      transactedBefore: getDateSchema(
        'endOf',
        'transactedBefore',
        requiredFields,
        'End date is required',
      ),
      amountGTE: AmountSchema.refine((val) => !requiredFields?.includes('amountGTE') || !!val, {
        message: 'Minimum amount is required',
      }),
      amountLTE: AmountSchema.refine((val) => !requiredFields?.includes('amountLTE') || !!val, {
        message: 'Maximum amount is required',
      }),
      status: z
        .array(z.enum(FILTER_STATUSES as readonly [string, ...string[]]))
        .refine((val) => !requiredFields?.includes('status') || val.length > 0, {
          message: 'At least one status is required',
        }),
      paymentMethodType: z.array(z.enum(PAYLINK_FILTER_PAYMENT_METHOD_TYPES)),
      customers: z
        .array(paylinkCustomerSchema)
        .refine((val) => !requiredFields?.includes('customers') || val.length > 0, {
          message: 'At least one customer is required',
        }),
    })
    .superRefine((data, ctx) => {
      if (
        data.createdAfter &&
        data.createdBefore &&
        data.createdAfter.isAfter(data.createdBefore)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start date must be before end date',
          path: ['createdAfter'],
        });
      }

      if (
        data.transactedAfter &&
        data.transactedBefore &&
        data.transactedAfter.isAfter(data.transactedBefore)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start date must be before end date',
          path: ['transactedAfter'],
        });
      }

      if (
        (data.transactedAfter || data.transactedBefore) &&
        data.status.some((status) => !TRANSACTED_FILTER_STATUSES.includes(status as PaylinkStatus))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Only Paid, Partially Paid, Refunded, Partially Refunded and ACH Returned statuses can be combined with a transaction date range',
          path: ['status'],
        });
      }

      if (
        data.amountGTE &&
        data.amountLTE &&
        Number(sanitizeNumericString(data.amountGTE)) >
          Number(sanitizeNumericString(data.amountLTE))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Minimum amount must be less than maximum amount',
          path: ['amountGTE'],
        });
      }

      if (dateRangeRequirement === 'createdOrTransactionExclusive') {
        const hasCreatedDate = !!(data.createdAfter || data.createdBefore);
        const hasTransactionDate = !!(data.transactedAfter || data.transactedBefore);

        if (hasCreatedDate && hasTransactionDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Use either Creation Date Range or Transaction Date Range, not both',
            path: ['createdAfter'],
          });
          return;
        }

        if (hasTransactionDate) {
          if (!data.transactedAfter) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Start date is required',
              path: ['transactedAfter'],
            });
          }
          if (!data.transactedBefore) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'End date is required',
              path: ['transactedBefore'],
            });
          }
          return;
        }

        if (!data.createdAfter) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Start date is required',
            path: ['createdAfter'],
          });
        }
        if (!data.createdBefore) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date is required',
            path: ['createdBefore'],
          });
        }
      }
    });

  return validationSchema;
};
type FormValues = z.infer<ReturnType<typeof getValidationSchema>>;

const emptyValues: FormValues = {
  createdAfter: null,
  createdBefore: null,
  transactedAfter: null,
  transactedBefore: null,
  amountGTE: '',
  amountLTE: '',
  status: [],
  paymentMethodType: [],
  customers: [],
};

interface Props {
  initialValues?: Partial<FormValues>;
  maxDateRange?: [number, dayjs.ManipulateType];
  requiredFields?: (keyof FormValues)[];
  dateRangeRequirement?: PaylinkFiltersDateRangeRequirement;
}

export const usePaylinkFiltersForm = ({
  initialValues,
  maxDateRange,
  requiredFields,
  dateRangeRequirement,
}: Props) => {
  return useForm<FormValues>({
    resolver: zodResolver(
      getValidationSchema(maxDateRange, requiredFields as string[], dateRangeRequirement),
    ),
    defaultValues: { ...emptyValues, ...initialValues },
    reValidateMode: 'onChange',
  });
};

export const paylinkFiltersFormValuesToApiFilters = (
  data: FormValues,
  paymentMethodFilterEnabled: boolean,
): GetAllPaylinksQueryFilters => {
  const filters: GetAllPaylinksQueryFilters = {};

  if (data.status.length > 0) filters.status = data.status as PaylinkStatus[];
  if (paymentMethodFilterEnabled && data.paymentMethodType.length > 0) {
    Object.assign(filters, toPaylinkPaymentMethodApiFilters(data.paymentMethodType));
  }
  if (data.createdAfter?.isValid()) filters.createdAfter = data.createdAfter.toISOString();
  if (data.createdBefore?.isValid()) filters.createdBefore = data.createdBefore.toISOString();
  if (data.transactedAfter?.isValid()) {
    filters.transactedAfter = data.transactedAfter.toISOString();
  }
  if (data.transactedBefore?.isValid()) {
    filters.transactedBefore = data.transactedBefore.toISOString();
  }
  if (data.amountGTE) filters.amountGTE = Number(sanitizeNumericString(data.amountGTE));
  if (data.amountLTE) filters.amountLTE = Number(sanitizeNumericString(data.amountLTE));
  if (data.customers.length > 0) {
    filters.customerIds = data.customers.map((c) => c.payLinkCustomerId);
  }

  return filters;
};

export const paylinkFiltersFormValuesToDownloadBody = (
  data: FormValues,
  paymentMethodFilterEnabled: boolean,
): Omit<DownloadPaylinksBody, 'merchantId'> => {
  const body: Omit<DownloadPaylinksBody, 'merchantId'> = {};

  if (data.amountGTE.length > 0) {
    body.amountFrom = Number(sanitizeNumericString(data.amountGTE));
  }
  if (data.amountLTE.length > 0) {
    body.amountTo = Number(sanitizeNumericString(data.amountLTE));
  }
  if (data.createdAfter?.isValid()) body.createdAfter = data.createdAfter.toISOString();
  if (data.createdBefore?.isValid()) body.createdBefore = data.createdBefore.toISOString();
  if (data.transactedAfter?.isValid()) {
    body.transactedAfter = data.transactedAfter.toISOString();
  }
  if (data.transactedBefore?.isValid()) {
    body.transactedBefore = data.transactedBefore.toISOString();
  }
  if (data.status.length > 0) body.paylinkStatus = data.status as PaylinkStatus[];
  if (paymentMethodFilterEnabled && data.paymentMethodType.length > 0) {
    Object.assign(body, toPaylinkPaymentMethodApiFilters(data.paymentMethodType));
  }
  if (data.customers.length > 0) {
    body.customers = data.customers.map((c) => c.payLinkCustomerId);
  }

  return body;
};

export type { FormValues as PaylinkFiltersFormValues };
export { FILTER_STATUSES as PAYLINK_FILTER_STATUSES, emptyValues as paylinkFiltersEmptyValues };
